import * as CL from '@design-system/component-library';
import { useRef } from 'react';
import { pushToDataLayer } from '../../common/analytics';
import {
  mBpsMsg,
  mobileRouter,
  recommendationUsersDevicesMsg,
  standalone5GMsg,
  t,
  uploadSpeedMsg,
} from '../../common/i18n';
import { AnalyticsListNames } from '../../common/utils/analyticsUtils';
import { getViewItemListEventForPlaceOrder } from '../PlaceOrder/utils/placeOrderAnalyticsUtils';
import {
  LiikkuvaWifiPopOverContent,
  ManagedServicePopoverContent,
  itsenainen5GVerkkoContent,
  reissunetti1GtContent,
  reissunetti2GtContent,
} from './HorizontalSubscriptionsContents';
import { SubscriptionCard } from './SubscriptionCard';
import { MobileCarouselWrapper } from './SubscriptionUtils';
import type { HorizontalMobileBroadbandSubscriptionsProps } from './HorizontalMobileBroadbandSubscriptions';
import type { SubCardWithAnalyticsV4Props } from './subscriptionCardsAnalyticsUtils';
import type { SubProps, SubscriptionCardWithModel } from './types';

export const HorizontalMBBLiikkuvaWifiSubscriptions = ({
  subscriptionCards,
  isChangeOffer = false,
  compact = false,
  onOrder,
  getCard,
  buttonText,
  showDescriptions = false,
  altDescription,
  hasQuantity = true,
  hideGroupHeaders = false,
  eventOrigin,
}: HorizontalMobileBroadbandSubscriptionsProps) => {
  const hasRunOnceRef = useRef(false);
  const analyticsListName = `${eventOrigin} ${AnalyticsListNames.MBB_LIIKUVA_WIFI_LIST_NAME}`;

  const sharedProps = {
    isChangeOffer,
    onOrder,
    subHeader: 'Yritysliittymä',
    buttonText: buttonText ?? t.B5J1('Add to basket'),
    buttonMakesOrder: true,
    analyticsListName,
    showFixedTermInfo: true,
    compact,
  };

  const LiikkuvaSub1000m = ({ analyticsIndex }: SubProps) => (
    <SubscriptionCard
      subscriptionCard={getCard('wifi1000m')}
      {...sharedProps}
      analyticsIndex={analyticsIndex}
      badges={[
        <CL.Badge key="5g" color="5g" text="5G+" />,
        <CL.Badge key="mobiilireititin" color="green-subtle" text={t.CV2A(mobileRouter)} />,
      ]}
      header="Liikkuva Wifi 1000 M"
      description={t.QF0D('For super-fast business use')}
      infoItems={[
        {
          icon: 'mobile-broadband',
          text: t.I00R('Includes a mobile router'),
          popoverContent: <LiikkuvaWifiPopOverContent />,
        },
        { icon: 'network', text: t.XWHX('Managed service'), popoverContent: <ManagedServicePopoverContent /> },
        {
          icon: 'group',
          text: t.SDTV('All internet users and devices'),
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '100')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '110'),
        },
      ]}
      hasQuantity={hasQuantity}
      additionalFeatures={[
        {
          icon: 'check',
          text: `Reissunetti 2${t.C4YQ('GB')}`,
          popoverContent: reissunetti2GtContent(),
        },
        {
          icon: 'check',
          text: t.ELB0(standalone5GMsg),
          popoverContent: itsenainen5GVerkkoContent(),
        },
      ]}
    />
  );

  const LiikkuvaSub600m = ({ analyticsIndex }: SubProps) => (
    <SubscriptionCard
      subscriptionCard={getCard('wifi600m')}
      {...sharedProps}
      analyticsIndex={analyticsIndex}
      badges={[
        <CL.Badge key="5g" color="5g" text="5G+" />,
        <CL.Badge key="mobiilireititin" color="green-subtle" text={t.CV2A(mobileRouter)} />,
      ]}
      header="Liikkuva Wifi 600 M"
      description={t.NXK9('For fast business use')}
      infoItems={[
        {
          icon: 'mobile-broadband',
          text: t.I00R('Includes a mobile router'),
          popoverContent: <LiikkuvaWifiPopOverContent />,
        },
        { icon: 'network', text: t.XWHX('Managed service'), popoverContent: <ManagedServicePopoverContent /> },
        {
          icon: 'group',
          text: t.SDTL(recommendationUsersDevicesMsg, '4+', '15+'),
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '75')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '90'),
        },
      ]}
      hasQuantity={hasQuantity}
      additionalFeatures={[
        {
          icon: 'check',
          text: `Reissunetti 1-1,5 ${t.C4YQ('GB')}`,
          popoverContent: reissunetti1GtContent(),
        },
        {
          icon: 'check',
          text: t.ELB0(standalone5GMsg),
          popoverContent: itsenainen5GVerkkoContent(),
        },
      ]}
    />
  );

  const LiikkuvaSub300m = ({ analyticsIndex }: SubProps) => (
    <SubscriptionCard
      subscriptionCard={getCard('wifi300m')}
      {...sharedProps}
      analyticsIndex={analyticsIndex}
      badges={[
        <CL.Badge key="5g" color="5g" text="5G+" />,
        <CL.Badge key="mobiilireititin" color="green-subtle" text={t.CV2A(mobileRouter)} />,
      ]}
      header="Liikkuva Wifi 300 M"
      description={t.EX8C('For active internet use')}
      infoItems={[
        {
          icon: 'mobile-broadband',
          text: t.I00R('Includes a mobile router'),
          popoverContent: <LiikkuvaWifiPopOverContent />,
        },
        { icon: 'network', text: t.XWHX('Managed service'), popoverContent: <ManagedServicePopoverContent /> },
        {
          icon: 'group',
          text: t.SDTL(recommendationUsersDevicesMsg, '3-4', '10-15'),
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '50')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '80'),
        },
      ]}
      hasQuantity={hasQuantity}
      additionalFeatures={[
        {
          icon: 'check',
          text: `Reissunetti 1-1,5 ${t.C4YQ('GB')}`,
          popoverContent: reissunetti1GtContent(),
        },
        {
          icon: 'check',
          text: t.ELB0(standalone5GMsg),
          popoverContent: itsenainen5GVerkkoContent(),
        },
      ]}
    />
  );

  let aIndex = 0;
  const liikkuvaWifiCards = isChangeOffer
    ? []
    : [
        { name: 'wifi1000m', component: LiikkuvaSub1000m },
        { name: 'wifi600m', component: LiikkuvaSub600m },
        { name: 'wifi300m', component: LiikkuvaSub300m },
      ].filter(card => !hideGroupHeaders || getCard(card.name));

  const filteredSubscriptionCards = (subscriptionCards as SubscriptionCardWithModel[]).filter(subCard =>
    liikkuvaWifiCards.some(card => card.name === subCard.cardName)
  );

  if (!hasRunOnceRef.current && filteredSubscriptionCards.every(subCard => subCard.onlineModel?.onlineModelName)) {
    const analyticsEvent = getViewItemListEventForPlaceOrder(
      filteredSubscriptionCards as SubCardWithAnalyticsV4Props[],
      analyticsListName
    );
    pushToDataLayer(analyticsEvent);
    hasRunOnceRef.current = true;
  }

  return (
    <>
      {hideGroupHeaders ? (
        <div className="of-subscriptions-main-container-header-spacer" />
      ) : (
        <h2 className="of-subscriptions-main-container-carousel-title">Liikkuva Wifi</h2>
      )}
      {showDescriptions && (
        <p className="ds-text--l">
          {altDescription
            ? altDescription
            : t.YTDQ(
                `Using the included mobile router saves your phone battery, as you don't have to use your phone as a hotspot. Thanks to 5G+ technology, the mobile router's battery use has been reduced by 15 to 25% compared to 4G or 5G networks.`
              )}
        </p>
      )}
      <MobileCarouselWrapper>
        {liikkuvaWifiCards.map(card => {
          const Component = card.component;
          return <Component key={card.name} analyticsIndex={aIndex++} />;
        })}
      </MobileCarouselWrapper>
    </>
  );
};
